"use client"

import React from "react"
import Image from "next/image"
import Link from "next/link"
import { motion, useReducedMotion } from "motion/react"
import * as Icons from "lucide-react"
import type { AboutSectionBlock as AboutSectionBlockProps } from "@/payload-types"
import RichText from "@/components/RichText"
import { cn } from "@/utilities/ui"
import { Container } from "@/app/(frontend)/components/ui/Container"

export const AboutSection: React.FC<AboutSectionBlockProps> = ({
	title,
	layout = "image-right",
	image,
	description,
	values,
	valuesLayout,
	ctaText,
	ctaLink,
}) => {
	const shouldReduceMotion = useReducedMotion()

	const containerVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				staggerChildren: 0.15,
			},
		},
	}

	const itemVariants = {
		hidden: { opacity: 0, y: shouldReduceMotion ? 0 : 20 },
		visible: {
			opacity: 1,
			y: 0,
			transition: {
				duration: 0.5,
				ease: [0.16, 1, 0.3, 1] as [number, number, number, number],
			},
		},
	}

	const isCentered = layout === "centered"
	const isImageLeft = layout === "image-left"

	return (
		<section className="py-24 bg-background overflow-hidden">
			<Container>
				<div
					className={cn(
						"grid gap-16 items-center",
						isCentered
							? "grid-cols-1 text-center"
							: "grid-cols-1 lg:grid-cols-2",
					)}
				>
					{/* Image Column */}
					{!isCentered && image && typeof image === "object" && (
						<motion.div
							initial={{ opacity: 0, x: isImageLeft ? -40 : 40 }}
							whileInView={{ opacity: 1, x: 0 }}
							viewport={{ once: true, margin: "-100px" }}
							transition={{
								duration: 0.8,
								ease: [0.16, 1, 0.3, 1] as [number, number, number, number],
							}}
							className={cn(
								"relative aspect-[4/5] md:aspect-square lg:aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl ring-1 ring-border/50",
								isImageLeft ? "lg:order-1" : "lg:order-2",
							)}
						>
							<Image
								src={image.url || ""}
								alt={image.alt || title}
								fill
								className="object-cover transition-transform duration-700 hover:scale-105"
								sizes="(max-width: 1024px) 100vw, 50vw"
								priority={false}
							/>
							<div className="absolute inset-0 bg-linear-to-t from-black/20 to-transparent pointer-events-none" />
						</motion.div>
					)}

					{/* Content Column */}
					<div
						className={cn(
							"flex flex-col",
							isCentered
								? "items-center max-w-4xl mx-auto"
								: isImageLeft
									? "lg:order-2"
									: "lg:order-1",
						)}
					>
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							transition={{
								duration: 0.6,
								ease: [0.16, 1, 0.3, 1] as [number, number, number, number],
							}}
						>
							<h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-8 tracking-tight balance">
								{title}
							</h2>
						</motion.div>

						{description && (
							<motion.div
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								viewport={{ once: true }}
								transition={{
									duration: 0.6,
									delay: 0.1,
									ease: [0.16, 1, 0.3, 1] as [number, number, number, number],
								}}
								className="mb-12"
							>
								<RichText
									data={description}
									enableGutter={false}
									className={cn(
										"text-muted-foreground text-lg md:text-xl leading-relaxed max-w-none",
										isCentered && "mx-auto",
									)}
								/>
							</motion.div>
						)}

						{/* Values List */}
						{values && values.length > 0 && (
							<motion.div
								variants={containerVariants}
								initial="hidden"
								whileInView="visible"
								viewport={{ once: true, margin: "-50px" }}
								className={cn(
									"grid gap-8 w-full",
									valuesLayout === "4"
										? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
										: valuesLayout === "3"
											? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
											: valuesLayout === "2"
												? "grid-cols-1 md:grid-cols-2"
												: valuesLayout === "1"
													? "grid-cols-1"
													: isCentered
														? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
														: "grid-cols-1 sm:grid-cols-2",
								)}
							>
								{values.map((value, index) => {
									const IconComponent = value.icon
										? (Icons as unknown as Record<string, React.ElementType>)[
												value.icon
											] || null
										: null
									return (
										<motion.div
											key={value.id || index}
											variants={itemVariants}
											className="group flex flex-col items-start text-left p-6 rounded-2xl bg-card border border-border transition-all duration-300 hover:shadow-lg hover:shadow-primary/5 hover:-translate-y-1"
										>
											{IconComponent && (
												<div className="mb-5 p-3 rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
													<IconComponent size={24} strokeWidth={2} />
												</div>
											)}
											<h3 className="text-xl font-bold text-foreground mb-3">
												{value.title}
											</h3>
											{value.description && (
												<p className="text-muted-foreground leading-relaxed text-sm md:text-base">
													{value.description}
												</p>
											)}
										</motion.div>
									)
								})}
							</motion.div>
						)}

						{ctaText && ctaLink && (
							<motion.div
								initial={{ opacity: 0, y: 20 }}
								whileInView={{ opacity: 1, y: 0 }}
								viewport={{ once: true }}
								transition={{
									duration: 0.6,
									delay: 0.3,
									ease: [0.16, 1, 0.3, 1] as [number, number, number, number],
								}}
							>
								<Link
									href={ctaLink}
									className="btn-primary px-10 py-4 text-lg font-semibold rounded-full shadow-xl shadow-primary/20 hover:shadow-primary/30 hover:-translate-y-1 active:translate-y-0 transition-all duration-300"
								>
									{ctaText}
								</Link>
							</motion.div>
						)}
					</div>
				</div>
			</Container>
		</section>
	)
}
